// Basket arithmetic for the client ordering portal. All money is integer pence;
// lines are treated as immutable so React state updates stay predictable.

export function addItem(lines, product, quantity = 1) {
  const existing = lines.find((l) => l.sku === product.sku);
  if (existing) {
    return lines.map((l) =>
      l.sku === product.sku ? { ...l, quantity: l.quantity + quantity } : l
    );
  }
  return [...lines, { ...product, quantity }];
}

export function removeItem(lines, sku) {
  return lines.filter((l) => l.sku !== sku);
}

export function setQuantity(lines, sku, quantity) {
  if (quantity <= 0) return removeItem(lines, sku);
  return lines.map((l) => (l.sku === sku ? { ...l, quantity } : l));
}

export function setLineNote(lines, sku, note) {
  return lines.map((l) => (l.sku === sku ? { ...l, note } : l));
}

export function basketTotals(lines) {
  return {
    item_count: lines.reduce((n, l) => n + l.quantity, 0),
    subtotal_pence: lines.reduce((n, l) => n + l.price_pence * l.quantity, 0),
  };
}

// The API body for POST /api/portal/orders.
//
// shown_price_pence is what the client was looking at, not what they are
// asking to pay. Prices are live, so the server prices every line from the
// current cost and compares; if the two differ it refuses the order and says
// what moved, rather than charging a figure the client never saw. Sending it
// is what makes that check possible.
export function toOrderPayload(lines, clientReference, notes) {
  return {
    lines: lines.map((l) => ({
      portal_product_id: l.id,
      quantity: l.quantity,
      ...(l.note && l.note.trim() ? { note: l.note.trim() } : {}),
      shown_price_pence: l.price_pence,
    })),
    client_reference: clientReference?.trim() || undefined,
    notes: notes?.trim() || undefined,
  };
}

// Bring a basket back in line with the catalogue as it is now, and say what
// moved. Called when the basket is opened, so a client sees a price change
// before they commit to it rather than having their order refused at submit.
export function reconcileBasket(lines, products) {
  const changes = [];
  const removed = [];
  const next = [];
  for (const l of lines) {
    const current = products.find((p) => p.id === l.id);
    if (!current) {
      removed.push({ sku: l.sku, name: l.name });
      continue;
    }
    if (current.price_pence !== l.price_pence) {
      changes.push({ sku: l.sku, name: l.name, was: l.price_pence, now: current.price_pence });
      next.push({ ...l, price_pence: current.price_pence });
      continue;
    }
    next.push(l);
  }
  return { lines: next, changes, removed };
}

export function formatPounds(pence) {
  return `£${(pence / 100).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
